import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from "@mui/material";
import React, { useMemo, useState } from "react";
import * as services from "../services/customer";
import * as localServices from "../services/local";
import Swal from "sweetalert2";
import { formatThaiId } from "../utils/formatters";
import PersonIcon from "@mui/icons-material/Person";
import BadgeIcon from "@mui/icons-material/Badge";
import CloseIcon from "@mui/icons-material/Close";
import DocumentScannerIcon from "@mui/icons-material/DocumentScanner";
import FaceIcon from "@mui/icons-material/Face";

const DriverFormDialog = ({
  open,
  onClose,
  onSuccess,
  customerData,
  initialData,
}) => {
  const isEdit = Boolean(initialData);

  const [name, setName] = useState(initialData?.name || "");
  const [surname, setSurname] = useState(initialData?.surname || "");
  const [idCard, setIdCard] = useState(initialData?.idCard || "");
  const [dob, setDob] = useState(initialData?.dob || "");
  const [address, setAddress] = useState(initialData?.address || "");
  const [photo, setPhoto] = useState(initialData?.img || "");

  const [saving, setSaving] = useState(false);

  const resetForm = () => {
    setName("");
    setSurname("");
    setIdCard("");
    setSaving(false);
  };

  // ฟังก์ชันอ่านบัตรประชาชน
  const readCard = async () => {
    try {
      const res = await localServices.readCard();
      const data = res.data.data;
      setName(data.firstNameTH || "");
      setSurname(data.lastNameTH || "");
      setIdCard(data.citizenId || "");
      setDob(data.birthday || "");
      setAddress(data.address || "");
      setPhoto(data.photo || "");

      console.log("ข้อมูลบัตร:", res.data);
    } catch (err) {
      console.error("ไม่สามารถอ่านบัตรได้:", err);
      alert("ไม่สามารถอ่านบัตรได้");
    }
  };

  // validate ชื่อขั้นต่ำ (นามสกุลและเลขบัตรไม่บังคับ)
  const isValidName = (v) => {
    const t = (v || "").trim();
    return t.length >= 2 && !/\d/.test(t);
  };

  const validate = () => {
    if (!isValidName(name)) {
      Swal.fire({
        icon: "warning",
        title: "ข้อมูลไม่ถูกต้อง",
        text: "กรุณากรอกชื่ออย่างน้อย 2 ตัวอักษร และไม่มีตัวเลข",
        customClass: { container: "my-swal" },
      });
      return false;
    }
    return true;
  };

  const canRudimentarySubmit = useMemo(() => !!name.trim(), [name]);

  const handleSubmit = async () => {
    if (!customerData?.id) {
      Swal.fire({
        icon: "error",
        title: "ไม่พบลูกค้า",
        text: "กรุณาลองใหม่อีกครั้ง",
        customClass: { container: "my-swal" },
      });
      return;
    }

    if (!validate()) return;
    let img;
    setSaving(true);
    if (photo && isBase64(photo)) {
      const blob = base64ToBlob(photo);
      const form = new FormData();
      form.append("file", blob, "image.png");
      let response = await services.upload(form);
      img = response.data.file;
    }
    try {
      const newDriver = {
        name: name.trim(),
        surname: surname.trim() || null,
        idCard: idCard.trim() || null,
        dob: dob || null,
        address: address || null,
        img: img || null,
        customerId: customerData.id,
      };

      const updatedDrivers = [...(customerData?.drivers || []), newDriver];
      const payload = { drivers: updatedDrivers };

      const res = await services.updateCustomer(payload, customerData.id, {
        includeMeta: false,
      });
      const updatedCustomer = res?.data?.data || res?.data;
      const savedDriver =
        updatedCustomer?.drivers?.find(
          (d) => d?.name === newDriver.name && d?.surname === newDriver.surname
        ) || newDriver;

      Swal.fire({
        icon: "success",
        title: isEdit ? "แก้ไขข้อมูลคนขับสำเร็จ" : "เพิ่มข้อมูลคนขับสำเร็จ",
        customClass: { container: "my-swal" },
      });

      onSuccess?.(savedDriver);
      resetForm();
      onClose?.();
    } catch (err) {
      console.error("Driver save failed:", err);
      Swal.fire({
        icon: "error",
        title: "เกิดข้อผิดพลาด",
        text: "ไม่สามารถบันทึกข้อมูลคนขับได้ กรุณาลองใหม่",
        customClass: { container: "my-swal" },
      });
      setSaving(false);
    }
  };
  function isBase64(str) {
    return (
      typeof str === "string" && /^data:image\/[a-zA-Z]+;base64,/.test(str)
    );
  }
  function base64ToBlob(base64, mimeType = "image/png") {
    const byteString = atob(base64.split(",")[1]);
    const ab = new ArrayBuffer(byteString.length);
    const ia = new Uint8Array(ab);
    for (let i = 0; i < byteString.length; i++) {
      ia[i] = byteString.charCodeAt(i);
    }
    return new Blob([ab], { type: mimeType });
  }

  const handleClose = () => {
    resetForm();
    onClose?.();
  };

  return (
    <Dialog
      open={!!open}
      onClose={handleClose}
      fullWidth
      maxWidth="sm"
      PaperProps={{
        sx: {
          borderRadius: "20px",
          overflow: "hidden",
        },
      }}
    >
      <DialogTitle className="bg-white p-4 border-b border-gray-100">
        <div className="flex justify-between items-center">
          <div className="flex items-center gap-3">
             <div className="w-10 h-10 rounded-full bg-indigo-50 flex items-center justify-center text-indigo-600">
                <PersonIcon />
             </div>
             <div>
                <h2 className="text-xl font-bold text-gray-800">
                    {isEdit ? "แก้ไขข้อมูลคนขับ" : "เพิ่มข้อมูลคนขับ"}
                </h2>
                <p className="text-sm text-gray-500 font-normal">
                    กรอกข้อมูลคนขับรถเพื่อบันทึกประวัติ
                </p>
             </div>
          </div>
          <button 
            onClick={handleClose}
            className="p-2 text-gray-400 hover:bg-gray-100 rounded-full transition-colors"
          >
            <CloseIcon />
          </button>
        </div>
      </DialogTitle>

      <DialogContent className="p-0">
        <div className="p-6 space-y-5">
            {/* Read Card Button */}
            <button
                onClick={readCard}
                className="w-full py-3 px-4 bg-indigo-50 hover:bg-indigo-100 border border-indigo-200 text-indigo-700 rounded-xl flex items-center justify-center gap-2 transition-all font-medium mb-2 group"
            >
                <div className="p-1 bg-white rounded-lg shadow-sm group-hover:scale-110 transition-transform">
                    <DocumentScannerIcon fontSize="small" />
                </div>
                ดึงข้อมูลจากบัตรประชาชน (Smart Card Reader)
            </button>
            
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                {/* ชื่อ */}
                <div className="space-y-1.5">
                    <label className="text-sm font-semibold text-gray-700 flex items-center gap-1">
                        <FaceIcon fontSize="small" className="text-gray-400" />
                        ชื่อจริง <span className="text-red-500">*</span>
                    </label>
                    <input
                        type="text"
                        value={name}
                        onChange={(e) => setName(e.target.value)}
                        placeholder="ระบุชื่อจริง"
                        className="w-full bg-gray-50 border border-gray-200 rounded-xl px-4 py-3 text-gray-700 focus:outline-none focus:ring-2 focus:ring-indigo-500 transition-all font-medium"
                    />
                    <p className="text-xs text-gray-400">อย่างน้อย 2 ตัวอักษร</p>
                </div>

                {/* นามสกุล */}
                <div className="space-y-1.5">
                    <label className="text-sm font-semibold text-gray-700 flex items-center gap-1">
                        <span className="w-5"></span> 
                        นามสกุล
                    </label>
                    <input
                        type="text"
                        value={surname}
                        onChange={(e) => setSurname(e.target.value)}
                        placeholder="ระบุนามสกุล"
                        className="w-full bg-gray-50 border border-gray-200 rounded-xl px-4 py-3 text-gray-700 focus:outline-none focus:ring-2 focus:ring-indigo-500 transition-all font-medium"
                    />
                </div>
            </div>

            {/* เลขบัตรประชาชน */}
            <div className="space-y-1.5">
                 <label className="text-sm font-semibold text-gray-700 flex items-center gap-1">
                    <BadgeIcon fontSize="small" className="text-gray-400" />
                    เลขบัตรประชาชน
                </label>
                <input
                    type="text"
                    value={formatThaiId(idCard)}
                    onChange={(e) => setIdCard(e.target.value.replace(/\D/g, "").slice(0, 13))}
                    maxLength={17}
                    placeholder="x-xxxx-xxxxx-xx-x"
                    className="w-full bg-gray-50 border border-gray-200 rounded-xl px-4 py-3 text-gray-700 focus:outline-none focus:ring-2 focus:ring-indigo-500 transition-all font-mono text-lg tracking-wide"
                />
            </div>
            
        </div>
      </DialogContent>

      <DialogActions className="p-4 border-t border-gray-100 bg-gray-50">
        <div className="flex gap-3 w-full justify-end">
            <button
                onClick={handleClose}
                className="px-6 py-2.5 rounded-xl border border-gray-200 text-gray-600 font-bold hover:bg-gray-100 transition-all text-sm"
                disabled={saving}
            >
                ยกเลิก
            </button>
            <button
                onClick={handleSubmit}
                disabled={!canRudimentarySubmit || saving}
                className="px-8 py-2.5 rounded-xl bg-indigo-600 text-white font-bold hover:bg-indigo-700 shadow-lg shadow-indigo-200 transition-all active:scale-95 disabled:opacity-50 disabled:shadow-none text-sm"
            >
                {saving ? (
                    <span className="flex items-center gap-2">
                        <svg className="animate-spin h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                        </svg>
                        กำลังบันทึก...
                    </span>
                ) : (
                    isEdit ? "บันทึกการแก้ไข" : "บันทึกข้อมูล"
                )}
            </button>
        </div>
      </DialogActions>
    </Dialog>
  );
};

export default DriverFormDialog;
